import { useState, useEffect } from 'react';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpdateConference } from '../../hooks';
import { Voicemail, TriangleAlert, Info } from 'lucide-react';
import type { DID } from '../../types';

interface ConferenceTabProps {
  did?: DID | null;
}

export function ConferenceTab({ did }: ConferenceTabProps) {
  const [enabled, setEnabled] = useState(false);
  const [pin, setPin] = useState('');
  const updateConference = useUpdateConference();

  useEffect(() => {
    if (did) {
      setEnabled(!!did.has_conferencing);
      setPin(did.conference_pin || '');
    }
  }, [did]);

  const handleSubmit = () => {
    if (!did) return;
    updateConference.mutate({
      tn: did.tn,
      enabled,
      pin: enabled && pin ? pin : undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-violet-50 rounded-xl">
          <Voicemail className="h-6 w-6 text-violet-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Conference Line</h3>
          <p className="text-sm text-slate-500 mt-1">
            Enable this DID as a conference bridge for multi-party calls.
          </p>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Mutual Exclusivity</p>
          <p className="text-sm text-amber-700">
            Only one of the following services may be enabled at a time:{' '}
            <strong>Forward, Failover, eFax, and Conference</strong>
          </p>
        </div>
      </div>

      {/* Info about disable behavior */}
      {enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
          <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Disabling Conference</p>
            <p className="text-sm text-blue-700">
              Disabling conference will automatically restore this DID's routing to your endpoint group.
            </p>
          </div>
        </div>
      )}

      {/* Info note */}
      <div className="bg-slate-100 rounded-lg p-3 flex gap-2">
        <Info className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">
          Keep in mind that line changes can take a few minutes to propagate through our system.
        </p>
      </div>

      {/* Toggle Card */}
      <div className="bg-slate-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900">Conference Line</p>
            <p className="text-sm text-slate-500">
              {enabled ? 'Conference bridge is active' : 'Conference is disabled'}
            </p>
          </div>
          <ToggleSwitch
            enabled={enabled}
            onChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="pt-4 border-t border-slate-200">
            <Input
              label="Conference PIN (Optional)"
              placeholder="Enter a PIN for conference access"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              maxLength={20}
              hint="Leave blank for no PIN requirement"
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={handleSubmit}
          isLoading={updateConference.isPending}
          disabled={!did}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
